/**
 * uPlot plugin: draw an "S" and split ratio in a box on bars where a split is in effect.
 * Used when the chart is displaying unadjusted data. Styling matches app (newsprint/ink/rule).
 */
import type uPlot from 'uplot';
import type { SplitChartDatum } from '../lib/chartTimeline';

const RULE = '#a39e94';
const NEWSPRINT_PANEL = '#f0ebe3';
const INK = '#1c1c1c';
const S_BOX_WIDTH = 32;
const S_BOX_HEIGHT = 24;
const BOX_RADIUS = 4;
const FONT_SIZE = 20;
const FONT = `${FONT_SIZE}px sans-serif`;
const TOP_OFFSET = 8;
const GAP = 10;

export type SplitAnnotationPluginOpts = {
  /** Returns chart indices and split ratios where a split occurs. */
  getSplitData: () => SplitChartDatum[];
  /** Fraction of chart height used for volume strip. Must match primary chart. */
  volumeStripRatio: number;
};

function formatRatio(ratio: number): string {
  if (ratio >= 1) return `${Math.round(ratio)}:1`;
  if (ratio > 0) return `1:${Math.round(1 / ratio)}`;
  return '—';
}

export function splitAnnotationPlugin(opts: SplitAnnotationPluginOpts): uPlot.Plugin {
  const { getSplitData } = opts;

  return {
    hooks: {
      draw: (u: uPlot) => {
        const data = getSplitData();
        if (data.length === 0) return;

        u.ctx.save();

        const { top, width } = u.bbox;
        const y = top + TOP_OFFSET;

        const idxs = u.series[0].idxs;
        if (!idxs) {
          u.ctx.restore();
          return;
        }
        const iMin = idxs[0];
        const iMax = idxs[1];

        u.ctx.font = FONT;
        u.ctx.textBaseline = 'middle';

        for (const { chartIndex: idx, ratio } of data) {
          if (idx < iMin || idx > iMax) continue;
          const xVal = u.scales.x.distr === 2 ? idx : (u.data[0][idx] as number);
          const xPx = u.valToPos(xVal, 'x', true);

          const ratioStr = formatRatio(ratio);
          if (xPx < -S_BOX_WIDTH / 2 || xPx > width + S_BOX_WIDTH / 2) continue;

          const boxLeft = xPx - S_BOX_WIDTH / 2;
          const top_ = y;

          u.ctx.fillStyle = NEWSPRINT_PANEL;
          u.ctx.strokeStyle = RULE;
          u.ctx.lineWidth = 1;
          roundRect(u.ctx, boxLeft, top_, S_BOX_WIDTH, S_BOX_HEIGHT, BOX_RADIUS);
          u.ctx.fill();
          u.ctx.stroke();

          u.ctx.fillStyle = INK;
          u.ctx.textAlign = 'center';
          u.ctx.fillText('S', boxLeft + S_BOX_WIDTH / 2, top_ + S_BOX_HEIGHT / 2);

          u.ctx.textAlign = 'left';
          u.ctx.fillText(ratioStr, boxLeft + S_BOX_WIDTH + GAP, top_ + S_BOX_HEIGHT / 2);
        }

        u.ctx.restore();
      },
    },
  };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
